import React, { useEffect, useState } from 'react';
import { Box, Typography, LinearProgress, useTheme } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { calculateRemainingTime, formatTime } from '../../utils/otpHelpers';

/**
 * Countdown timer for OTP expiration
 * @param {Object} props
 * @param {string} props.expiresAt - ISO 8601 datetime string
 * @param {Function} props.onExpire - Callback when timer expires
 * @param {boolean} props.showProgress - Show progress bar
 */
const OtpCountdownTimer = ({
    expiresAt,
    onExpire,
    showProgress = true
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const totalSeconds = 5 * 60; // 5 minutes total

    const onExpireRef = React.useRef(onExpire);
    const lastHandledExpireAt = React.useRef(null);

    useEffect(() => {
        onExpireRef.current = onExpire;
    }, [onExpire]);

    useEffect(() => {
        if (!expiresAt) return;

        // Reset if we have a new expiration time
        if (lastHandledExpireAt.current !== expiresAt) {
            lastHandledExpireAt.current = null;
        }

        // Initial calculation
        const initial = calculateRemainingTime(expiresAt);
        setRemainingSeconds(initial);

        if (initial <= 0) {
            if (onExpireRef.current && lastHandledExpireAt.current !== expiresAt) {
                lastHandledExpireAt.current = expiresAt;
                onExpireRef.current();
            }
            return;
        }

        // Update every second
        const interval = setInterval(() => {
            const remaining = calculateRemainingTime(expiresAt);
            setRemainingSeconds(remaining);

            if (remaining <= 0) {
                clearInterval(interval);
                if (onExpireRef.current && lastHandledExpireAt.current !== expiresAt) {
                    lastHandledExpireAt.current = expiresAt;
                    onExpireRef.current();
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [expiresAt]);

    if (!expiresAt || remainingSeconds <= 0) {
        return null;
    }

    const percentage = (remainingSeconds / totalSeconds) * 100;
    const isLowTime = remainingSeconds < 60; // Less than 1 minute
    const isVeryLowTime = remainingSeconds < 30; // Less than 30 seconds

    const getColor = () => {
        if (isVeryLowTime) return theme.palette.error.main;
        if (isLowTime) return theme.palette.warning.main;
        return theme.palette.primary.main;
    };

    return (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mb: showProgress ? 1 : 0
                }}
            >
                <AccessTime
                    sx={{
                        fontSize: '1.25rem',
                        color: getColor()
                    }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: getColor(),
                        fontSize: '1rem'
                    }}
                >
                    {formatTime(remainingSeconds)}
                </Typography>
            </Box>

            {showProgress && (
                <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.action.hover,
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: getColor(),
                            borderRadius: 3,
                        }
                    }}
                />
            )}

            {isLowTime && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 0.5,
                        color: getColor(),
                        fontWeight: 500
                    }}
                >
                    {isVeryLowTime ? t('otp.ending_soon') : t('otp.time_running_out')}
                </Typography>
            )}
        </Box>
    );
};

export default OtpCountdownTimer;
